import {
  createContext,
  useCallback,
  useContext,
  useRef,
  type ReactNode,
} from 'react';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import { useNotificationPreferences } from '../stores/useNotificationPreferences';

export interface ToastApi {
  showInfo: (content: string, title?: string) => void;
  showError: (content: string, title?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const toastRef = useRef<ToastComponent>(null);
  const silenced = useNotificationPreferences((s) => s.silenced);

  const show = useCallback(
    (content: string, title: string, cssClass: string, timeOut: number, respectSilence: boolean) => {
      if (respectSilence && silenced) return;
      toastRef.current?.show({
        title,
        content,
        cssClass,
        timeOut,
      });
    },
    [silenced]
  );

  const api: ToastApi = {
    showInfo: (content, title = 'Notification') =>
      show(content, title, 'e-toast-info', 3000, true),
    showError: (content, title = 'Error') =>
      show(content, title, 'e-toast-danger', 5000, false),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastComponent ref={toastRef} position={{ X: 'Right', Y: 'Top' }} newestOnTop />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
